import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { toast } from 'sonner';
import { 
  MapPin, Clock, Camera, CheckCircle, Play, ArrowRight, 
  Navigation, Phone, FileText, LogOut, ChevronRight
} from 'lucide-react';
import axios from 'axios';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_property-dispatch/artifacts/zkteztq8_OrchardLMS%20Logo.png";

const CrewMobilePage = () => {
  const { user, token, API, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [properties, setProperties] = useState({});

  useEffect(() => {
    fetchTodayJobs();
  }, []);

  const fetchTodayJobs = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.get(`${API}/jobs?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setJobs(response.data.jobs);

      // Fetch properties for jobs
      const propIds = [...new Set(response.data.jobs.map(j => j.property_id))];
      if (propIds.length > 0) {
        const propsRes = await axios.get(`${API}/properties`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const propMap = {};
        propsRes.data.properties.forEach(p => propMap[p.id] = p);
        setProperties(propMap);
      }
    } catch (error) {
      toast.error('Failed to fetch jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleStartJob = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        status: 'in_progress',
        actual_start_time: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job started!');
      fetchTodayJobs();
    } catch (error) {
      toast.error('Failed to start job');
    }
  };

  const handleCompleteJob = async (jobId) => {
    try {
      await axios.put(`${API}/jobs/${jobId}`, {
        status: 'completed',
        actual_end_time: new Date().toISOString()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Job completed!');
      setSelectedJob(null);
      fetchTodayJobs();
    } catch (error) {
      toast.error('Failed to complete job');
    }
  };

  const getProperty = (propertyId) => properties[propertyId] || {};

  const getStatusColor = (status) => {
    const colors = {
      scheduled: 'bg-blue-500',
      in_progress: 'bg-amber-500',
      completed: 'bg-emerald-500'
    };
    return colors[status] || 'bg-gray-500';
  };

  const completedCount = jobs.filter(j => j.status === 'completed').length;
  const remainingCount = jobs.filter(j => j.status !== 'completed').length;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Job Detail View
  if (selectedJob) {
    const job = selectedJob;
    const property = getProperty(job.property_id);

    return (
      <div className="min-h-screen bg-gray-100">
        {/* Header */}
        <header className="bg-primary text-white px-4 py-3 sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <button onClick={() => setSelectedJob(null)} className="flex items-center gap-2">
              <ArrowRight className="h-5 w-5 rotate-180" />
              <span>Back</span>
            </button>
            <span className="font-medium">Job Details</span>
            <div className="w-16"></div>
          </div>
        </header>

        <div className="p-4 space-y-4">
          {/* Property Card */}
          <Card className="border-0 shadow-md">
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h2 className="font-semibold text-lg text-gray-900">{property.address}</h2>
                  <p className="text-gray-500">{property.city}, {property.state}</p>
                </div>
                <span className={`${getStatusColor(job.status)} text-white text-xs px-2 py-1 rounded-full`}>
                  {job.status.replace('_', ' ')}
                </span>
              </div>

              {property.gate_code && (
                <div className="flex items-center gap-2 text-sm text-gray-600 mb-2">
                  <span className="font-medium">Gate Code:</span>
                  <span className="bg-gray-100 px-2 py-1 rounded">{property.gate_code}</span>
                </div>
              )}

              {property.service_instructions && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 mb-1">Service Instructions</p>
                  <p className="text-sm text-blue-700">{property.service_instructions}</p>
                </div>
              )}

              {property.access_notes && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 mb-1">Access Notes</p>
                  <p className="text-sm text-amber-700">{property.access_notes}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <Button 
              variant="outline" 
              className="h-14 flex flex-col items-center gap-1"
              onClick={() => {
                if (property.latitude && property.longitude) {
                  window.open(`https://maps.google.com/maps?daddr=${property.latitude},${property.longitude}`, '_blank');
                } else {
                  window.open(`https://maps.google.com/maps?daddr=${encodeURIComponent(property.address)}`, '_blank');
                }
              }}
            >
              <Navigation className="h-5 w-5 text-primary" />
              <span className="text-xs">Navigate</span>
            </Button>
            <Button variant="outline" className="h-14 flex flex-col items-center gap-1">
              <Phone className="h-5 w-5 text-primary" />
              <span className="text-xs">Call Customer</span>
            </Button>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3 pt-4">
            {job.status === 'scheduled' && (
              <Button 
                className="w-full h-14 bg-primary hover:bg-primary/90 text-lg"
                onClick={() => handleStartJob(job.id)}
                data-testid="start-job-btn"
              >
                <Play className="h-5 w-5 mr-2" />
                Start Job
              </Button>
            )}

            {job.status === 'in_progress' && (
              <>
                <Button variant="outline" className="w-full h-12">
                  <Camera className="h-5 w-5 mr-2" />
                  Take Photo
                </Button>
                <Button 
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-lg"
                  onClick={() => handleCompleteJob(job.id)}
                  data-testid="complete-job-btn"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Complete Job
                </Button>
              </>
            )}

            {job.status === 'completed' && (
              <div className="text-center py-4">
                <CheckCircle className="h-16 w-16 text-emerald-500 mx-auto mb-2" />
                <p className="text-lg font-medium text-emerald-600">Job Completed</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Jobs List View
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-primary text-white px-4 py-4 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-4">
          <img src={LOGO_URL} alt="Orchard LMS" className="h-8 brightness-0 invert" />
          <button onClick={logout} className="p-2">
            <LogOut className="h-5 w-5" />
          </button>
        </div>
        <div>
          <p className="text-white/80 text-sm">Welcome back,</p>
          <p className="text-xl font-semibold">{user?.name}</p>
        </div>
      </header>

      {/* Stats */}
      <div className="px-4 py-4">
        <div className="grid grid-cols-2 gap-3">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-primary">{remainingCount}</p>
              <p className="text-sm text-gray-500">Remaining</p>
            </CardContent>
          </Card>
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4 text-center">
              <p className="text-3xl font-bold text-emerald-600">{completedCount}</p>
              <p className="text-sm text-gray-500">Completed</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Today's Jobs */}
      <div className="px-4 pb-4">
        <h2 className="font-semibold text-gray-900 mb-3">Today's Schedule</h2>
        
        {jobs.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">No jobs scheduled for today</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {jobs.map((job) => {
              const property = getProperty(job.property_id);
              return (
                <Card 
                  key={job.id} 
                  className="border-0 shadow-sm cursor-pointer active:scale-[0.98] transition-transform"
                  onClick={() => setSelectedJob(job)}
                  data-testid={`job-card-${job.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`w-2 h-2 rounded-full ${getStatusColor(job.status)}`}></span>
                          <span className="text-xs text-gray-500 capitalize">{job.status.replace('_', ' ')}</span>
                        </div>
                        <h3 className="font-medium text-gray-900">{property.address || 'Loading...'}</h3>
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <Clock className="h-3 w-3" />
                          <span>{job.scheduled_time || 'Anytime'}</span>
                        </div>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default CrewMobilePage;
